import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Grid,
  MenuItem,
  TextField,
  Button,
  Divider,
  Box,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import toast from "react-hot-toast";

const GoodsTransfer = () => {
  const { company_id } = useSelector((state) => state.GlobalVariables);

  const [itemsList, setItemsList] = useState([]);
  const [locationList, setLocationList] = useState([]);

  const [rows, setRows] = useState([
    {
      item_id: "",
      current_location_id: "",
      current_location_name: "",
      actualquantity: 0,
      transfer_location_id: "",
      transfer_qty: "",
    },
  ]);

  // -------- FETCH ITEMS --------
  const fetchItemList = async () => {
    const res = await configServ.getItemList({ company_id });
    if (res.status === 200) {
      setItemsList(res.data);
    }
  };

  // -------- FETCH LOCATIONS --------
  const fetchLocation = async () => {
    const res = await configServ.getLocation({ company_id });
    const active = res.filter((x) => x.status === 1);
    setLocationList(active);
  };

  useEffect(() => {
    fetchItemList();
    fetchLocation();
  }, []);

  // -------- ITEM SELECT --------
  const handleItemChange = (index, itemId) => {
    const selectedItem = itemsList.find((x) => x.id === itemId);

    const updated = [...rows];
    updated[index] = {
      ...updated[index],
      item_id: itemId,
      current_location_id: selectedItem?.location_id || "",
      current_location_name: selectedItem?.city || "",
      actualquantity: selectedItem?.actualquantity || 0,
      transfer_location_id: "",
      transfer_qty: "",
    };

    setRows(updated);
  };

  // -------- TRANSFER LOCATION --------
  const handleTransferLocation = (index, locationId) => {
    const updated = [...rows];
    updated[index].transfer_location_id = locationId;
    setRows(updated);
  };

  // -------- TRANSFER QTY --------
  const handleQtyChange = (index, value) => {
    const updated = [...rows];
    updated[index].transfer_qty = value;
    setRows(updated);
  };

  // -------- ADD ROW --------
  const addRow = () => {
    setRows([
      ...rows,
      {
        item_id: "",
        current_location_id: "",
        current_location_name: "",
        actualquantity: 0,
        transfer_location_id: "",
        transfer_qty: "",
      },
    ]);
  };

  const handleReset = () => {
    setRows([
      {
        item_id: "",
        current_location_id: "",
        current_location_name: "",
        actualquantity: 0,
        transfer_location_id: "",
        transfer_qty: "",
      },
    ]); 
  }

  // -------- REMOVE ROW --------
  const removeRow = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    setRows(updated);
  };

  // -------- VALIDATION --------
  const validate = () => {
    for (let row of rows) {
      if (!row.item_id) {
        toast.error("Select item");
        return false;
      }

      if (!row.transfer_location_id) {
        toast.error("Select transfer location");
        return false;
      }

      if (row.current_location_id === row.transfer_location_id) {
        toast.error("Transfer location cannot be same as current location");
        return false;
      }

      if (Number(row.transfer_qty) > Number(row.actualquantity)) {
        toast.error("Transfer qty cannot exceed available qty");
        return false;
      }
    }
    return true;
  };

  // -------- SUBMIT --------
  const handleSubmit = async () => {
  if (!validate()) return;

  const payload = {
    company_id: company_id,
    items: rows,   // ✅ rows ko items ke andar bhejna hai
  };

  try {
    const res = await configServ.createGoodsTransfer(payload);

    if (res.status === 200) {
      toast.success("Goods transfer successful ✅");
      handleReset();
    } else {
      toast.error(res.message || "Something went wrong");
    }

  } catch (error) {
    console.log("Transfer Error:", error);
    toast.error("Server error occurred ❌");
  }
};

  return (
    <Box width="100%"  sx={{ mt: 4 }}>
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1976d2" }}>
          Goods Transfer
        </Typography>
        <Divider sx={{ my: 3 }} />

        {rows.map((row, index) => (
          <Box key={index} sx={{ mb: 3, p: 2, border: "1px solid #eee", borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">

              {/* ITEM SELECT */}
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Item"
                  value={row.item_id}
                  onChange={(e) =>
                    handleItemChange(index, Number(e.target.value))
                  }
                >
                  {itemsList.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.item_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* CURRENT LOCATION */}
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Current Location"
                  value={row.current_location_name}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* AVAILABLE QTY */}
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label="Available Qty"
                  value={row.actualquantity}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* TRANSFER LOCATION */}
              <Grid item xs={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Transfer Location"
                  value={row.transfer_location_id}
                  onChange={(e) =>
                    handleTransferLocation(index, Number(e.target.value))
                  }
                >
                  {locationList.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.location_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* TRANSFER QTY */}
              <Grid item xs={4} md={1.5}>
                <TextField
                  fullWidth
                  label="Qty"
                  type="number"
                  value={row.transfer_qty}
                  onChange={(e) =>
                    handleQtyChange(index, e.target.value)
                  }
                />
              </Grid>

              {/* DELETE */}
              <Grid item xs={2} md={0.5}>
                <IconButton
                  color="error"
                  onClick={() => removeRow(index)}
                  disabled={rows.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button startIcon={<AddIcon />} onClick={addRow}>
          Add Item
        </Button>

        <Box sx={{ mt: 4, textAlign: "right" }}>
          <Button variant="contained" size="large" onClick={handleSubmit}>
            Transfer Goods
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default GoodsTransfer;